const meta = (list) => {
    const byValue = Object.fromEntries(list.map((s) => [s.value, s]));
    return { list, byValue, values: list.map((s) => s.value) };
};
export const CRM_STAGE = meta([
    { value: 'site_survey', label: 'Site survey', tone: 'info', description: 'Sales is surveying the site and capturing requirements' },
    { value: 'boq', label: 'BOQ', tone: 'progress', description: 'The technical team is preparing the Bill of Quantities' },
    { value: 'pricing', label: 'Pricing', tone: 'warning', description: 'The pricing manager is costing the approved BOQ' },
    { value: 'quotation', label: 'Quotation / PI', tone: 'info', description: 'Sales is preparing and negotiating the quotation' },
    { value: 'approval', label: 'Customer approval', tone: 'warning', description: 'Quotation is with the customer for approval' },
    { value: 'procurement', label: 'Procurement', tone: 'progress', description: 'Stock is being reserved and purchased' },
    { value: 'finance', label: 'Invoice & delivery', tone: 'info', description: 'Goods are ready: dispatch, invoice and collect' },
    { value: 'completed', label: 'Completed', tone: 'success', description: 'Delivered, installed and handed over' },
]);
/** Order of the pipeline, left to right on the Kanban board. */
export const CRM_STAGE_ORDER = CRM_STAGE.values;
/** Which role-team owns the work at each stage (shown on the board and used for notifications). */
export const CRM_STAGE_OWNER = {
    site_survey: 'Sales', boq: 'Technical', pricing: 'Pricing', quotation: 'Sales',
    approval: 'Sales', procurement: 'Procurement', finance: 'Finance', completed: 'Management',
};
export const CRM_PROJECT_STATUS = meta([
    { value: 'active', label: 'Active', tone: 'success', description: 'In the pipeline' },
    { value: 'on_hold', label: 'On hold', tone: 'warning', description: 'Paused; no stage can advance' },
    { value: 'lost', label: 'Lost', tone: 'danger', description: 'Customer rejected or went elsewhere' },
    { value: 'cancelled', label: 'Cancelled', tone: 'neutral', description: 'Closed internally' },
    { value: 'completed', label: 'Completed', tone: 'info', description: 'Delivered and handed over' },
]);
export const LEAD_STATUS = meta([
    { value: 'new', label: 'New', tone: 'info', description: 'Not yet contacted' },
    { value: 'contacted', label: 'Contacted', tone: 'progress', description: 'First conversation held' },
    { value: 'qualified', label: 'Qualified', tone: 'warning', description: 'Budget, need and timeline confirmed; ready to convert' },
    { value: 'converted', label: 'Converted', tone: 'success', description: 'Became a project' },
    { value: 'lost', label: 'Lost', tone: 'danger', description: 'Closed without a project' },
]);
export const LEAD_TRANSITIONS = {
    new: ['contacted', 'qualified', 'lost'],
    contacted: ['qualified', 'lost'],
    qualified: ['contacted', 'lost'], // converting is its own action, not a status edit
    converted: [],
    lost: ['new'], // a lost lead may be reopened
};
export const LEAD_SOURCES = ['Website', 'Referral', 'Walk-in', 'Cold call', 'Exhibition', 'Existing customer', 'Tender', 'Partner'];
export const INDUSTRIES = ['IT / ITES', 'Manufacturing', 'Healthcare', 'Education', 'Hospitality', 'Retail', 'BFSI', 'Government', 'Real estate', 'Logistics', 'Other'];
export const PROJECT_TYPES = ['New installation', 'Upgrade / expansion', 'Replacement', 'AMC / service', 'Turnkey'];
export const SURVEY_STATUS = meta([
    { value: 'scheduled', label: 'Scheduled', tone: 'info', description: 'Visit planned' },
    { value: 'in_progress', label: 'In progress', tone: 'progress', description: 'Findings being recorded' },
    { value: 'submitted', label: 'Submitted', tone: 'success', description: 'Sent to the technical team' },
    { value: 'cancelled', label: 'Cancelled', tone: 'neutral', description: 'Visit cancelled' },
]);
export const BOQ_STATUS = meta([
    { value: 'draft', label: 'Draft', tone: 'neutral', description: 'Being prepared' },
    { value: 'in_review', label: 'Internal review', tone: 'warning', description: 'Awaiting technical approval' },
    { value: 'approved', label: 'Approved', tone: 'info', description: 'Technically approved, not yet sent for pricing' },
    { value: 'submitted', label: 'With pricing', tone: 'progress', description: 'Submitted to the pricing manager' },
    { value: 'superseded', label: 'Superseded', tone: 'neutral', description: 'Replaced by a later revision' },
]);
export const PRICING_STATUS = meta([
    { value: 'draft', label: 'Draft', tone: 'neutral', description: 'Being worked on by the pricing manager' },
    { value: 'approved', label: 'Approved', tone: 'success', description: 'Released to Sales' },
    { value: 'superseded', label: 'Superseded', tone: 'neutral', description: 'Replaced by a later BOQ revision' },
]);
export const QUOTE_STATUS = meta([
    { value: 'draft', label: 'Draft', tone: 'neutral', description: 'Not yet sent to the customer' },
    { value: 'submitted', label: 'Submitted', tone: 'info', description: 'Sent to the customer' },
    { value: 'viewed', label: 'Viewed', tone: 'progress', description: 'The customer has opened it' },
    { value: 'negotiation', label: 'Negotiation', tone: 'warning', description: 'Terms or price under discussion' },
    { value: 'approved', label: 'Approved', tone: 'success', description: 'Customer accepted; locked' },
    { value: 'rejected', label: 'Rejected', tone: 'danger', description: 'Customer declined' },
    { value: 'superseded', label: 'Superseded', tone: 'neutral', description: 'Replaced by a later revision' },
]);
export const QUOTE_TRANSITIONS = {
    draft: ['submitted'],
    submitted: ['viewed', 'negotiation', 'approved', 'rejected'],
    viewed: ['negotiation', 'approved', 'rejected'],
    negotiation: ['approved', 'rejected', 'submitted'],
    approved: [],
    rejected: [],
    superseded: [],
};
export const PR_STATUS = meta([
    { value: 'open', label: 'Open', tone: 'warning', description: 'Waiting to be converted to a purchase order' },
    { value: 'ordered', label: 'Ordered', tone: 'progress', description: 'Purchase order raised' },
    { value: 'closed', label: 'Closed', tone: 'success', description: 'Goods received' },
    { value: 'cancelled', label: 'Cancelled', tone: 'neutral', description: 'No longer needed' },
]);
export const PO_STATUS = meta([
    { value: 'draft', label: 'Draft', tone: 'neutral', description: 'Not yet sent to the vendor' },
    { value: 'issued', label: 'Issued', tone: 'info', description: 'Sent; awaiting vendor confirmation' },
    { value: 'confirmed', label: 'Vendor confirmed', tone: 'progress', description: 'Vendor has confirmed with a delivery date' },
    { value: 'partially_received', label: 'Partly received', tone: 'warning', description: 'Some goods received' },
    { value: 'received', label: 'Received', tone: 'success', description: 'All goods received' },
    { value: 'cancelled', label: 'Cancelled', tone: 'neutral', description: 'Withdrawn' },
]);
export const PROCUREMENT_STATUS = meta([
    { value: 'in_stock', label: 'In stock', tone: 'success', description: 'Available to reserve' },
    { value: 'low_stock', label: 'Low stock', tone: 'warning', description: 'At or below the reorder level' },
    { value: 'out_of_stock', label: 'Out of stock', tone: 'danger', description: 'Nothing available; must be purchased' },
    { value: 'on_order', label: 'On order', tone: 'progress', description: 'A purchase order is open' },
    { value: 'discontinued', label: 'Discontinued', tone: 'neutral', description: 'No longer sold' },
]);
export const INVOICE_STATUS = meta([
    { value: 'draft', label: 'Draft', tone: 'neutral', description: 'Prepared, not issued' },
    { value: 'issued', label: 'Issued', tone: 'info', description: 'Sent to the customer; unpaid' },
    { value: 'partially_paid', label: 'Partly paid', tone: 'warning', description: 'Some payment received' },
    { value: 'paid', label: 'Paid', tone: 'success', description: 'Settled in full' },
    { value: 'cancelled', label: 'Cancelled', tone: 'neutral', description: 'Voided' },
]);
export const TALLY_STATUS = meta([
    { value: 'not_synced', label: 'Not synced', tone: 'neutral', description: 'Nothing sent to Tally yet' },
    { value: 'queued', label: 'Queued', tone: 'info', description: 'Voucher prepared; Tally endpoint not configured or not yet reached' },
    { value: 'synced', label: 'Synced', tone: 'success', description: 'Tally accepted the voucher' },
    { value: 'failed', label: 'Failed', tone: 'danger', description: 'Tally rejected the voucher or could not be reached' },
]);
export const INSTALLATION_STATUS = meta([
    { value: 'not_started', label: 'Not started', tone: 'neutral', description: 'Installation not begun' },
    { value: 'in_progress', label: 'In progress', tone: 'progress', description: 'Installation under way' },
    { value: 'completed', label: 'Completed', tone: 'success', description: 'Installed and commissioned' },
]);
// ---------------------------------------------------------------------
// Pricing arithmetic — pure, so the portal can preview exactly what the
// API will store, and a test can pin the numbers.
// ---------------------------------------------------------------------
export const DEFAULT_GST_RATE = 18;
export const GST_RATES = [0, 5, 12, 18, 28];
/** Pricing rules. Breaching either needs a Management sign-off to approve. */
export const PRICING_RULES = {
    /** Largest discount (% of goods value) the pricing manager may grant alone. */
    maxDiscountPct: 10,
    /** Lowest overall margin (% of taxable value) the pricing manager may approve alone. */
    minMarginPct: 8,
};
export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
export function computePricing(input) {
    const goodsSubtotal = round2(input.lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0));
    const requested = input.discount
        ? (input.discount.mode === 'percent' ? goodsSubtotal * (input.discount.value / 100) : input.discount.value)
        : 0;
    const discountAmount = round2(Math.min(Math.max(requested, 0), goodsSubtotal));
    const ratio = goodsSubtotal > 0 ? discountAmount / goodsSubtotal : 0;
    // The discount is spread pro rata so each line is taxed at its own GST rate
    // on what the customer actually pays for it.
    const lines = input.lines.map((l) => {
        const lineSubtotal = round2(l.quantity * l.unitPrice);
        const discount = round2(lineSubtotal * ratio);
        const taxable = round2(lineSubtotal - discount);
        const gst = round2(taxable * (l.gstRate / 100));
        return { lineSubtotal, discount, taxable, gst, total: round2(taxable + gst), cost: round2(l.quantity * l.unitCost) };
    });
    const inst = input.installation, svc = input.service;
    const installationAmount = round2(inst?.amount ?? 0);
    const serviceAmount = round2(svc?.amount ?? 0);
    const instGst = round2(installationAmount * ((inst?.gstRate ?? DEFAULT_GST_RATE) / 100));
    const svcGst = round2(serviceAmount * ((svc?.gstRate ?? DEFAULT_GST_RATE) / 100));
    const taxableTotal = round2(lines.reduce((s, l) => s + l.taxable, 0) + installationAmount + serviceAmount);
    const gstTotal = round2(lines.reduce((s, l) => s + l.gst, 0) + instGst + svcGst);
    const costTotal = round2(lines.reduce((s, l) => s + l.cost, 0) + (inst?.cost ?? 0) + (svc?.cost ?? 0));
    const marginAmount = round2(taxableTotal - costTotal);
    const marginPct = taxableTotal > 0 ? round2((marginAmount / taxableTotal) * 100) : 0;
    const discountPct = goodsSubtotal > 0 ? round2((discountAmount / goodsSubtotal) * 100) : 0;
    const breaches = [];
    if (discountPct > PRICING_RULES.maxDiscountPct)
        breaches.push('discount');
    if (taxableTotal > 0 && marginPct < PRICING_RULES.minMarginPct)
        breaches.push('margin');
    return {
        lines, goodsSubtotal, discountAmount, discountPct, installationAmount, serviceAmount,
        taxableTotal, gstTotal, grandTotal: round2(taxableTotal + gstTotal),
        costTotal, marginAmount, marginPct, breaches,
    };
}
/** Margin of a catalogue item, as a percentage of selling price. */
export const itemMarginPct = (cost, price) => price > 0 ? round2(((price - cost) / price) * 100) : 0;
//# sourceMappingURL=crm.js.map